import ModalHeader from '@molecules/ModalHeader';
import ModalWindow from '@organisms/modals/ModalWindow';
import {FC, useEffect, useState} from 'react';
import {ButtonsWrapper, ModalContent, ModalWrapper} from './EditAccessRightsModal.styles';
import Button from '@atoms/Button';
import {theme} from '@styles/theme';
import Text from '@atoms/Text';
import {users, roles} from '@store/slices/users';
import {IRole, ISubsystem, IUser} from '@types';
import {useDispatch, useSelector} from 'react-redux';
import MultiSelect from '@atoms/MultiSelect';
import Avatar from '@atoms/Avatar';
import OptionButton from '@atoms/OptionButton';
import {addSubsystem, editSubsystem} from '@store/slices/systems';
import Input from '@atoms/Input';
import Toast from '@atoms/Toast';
import {toast} from 'react-toastify';
import {
  AddSubsystemUsersWrapper,
  DefaultUsersInfoWrapper,
  DefaultUsersRow,
  DefaultUsersTextWrapper,
  DefaultUsersWrapper,
  SelectedUsersInfoWrapper,
  SelectedUsersRow,
  SelectedUsersTextWrapper,
  SelectedUsersWrapper,
  SubdirectoryWrapper,
} from '@organisms/modals/AddEditSubsystemModal.styles';
import {Subdirectory} from '@carbon/icons-react';
import {AppDispatch} from '@store';
import Tooltip from '@atoms/Tooltip';
import {EUserRoles, EUserScopedRoles, userScopedRoles} from '@constants/usersRoles';

interface Props {
  isOpened: boolean;
  onCloseRequest: () => void;
  systemID?: number;
  administrators?: IUser[];
  subsystem?: ISubsystem;
  currentScopedRole?: number;
  currentRole?: number;
}

const AddEditSubsystemModal: FC<Props> = ({
  isOpened,
  onCloseRequest,
  systemID,
  administrators,
  subsystem,
  currentScopedRole,
  currentRole,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [tooltipVisibleId, setTooltipVisibleId] = useState<number | null>(null);
  const [subsystemName, setSubsystemName] = useState<string>('');
  const [selectedUsersIDs, setSelectedUsersIDs] = useState<string[]>(
    subsystem?.users?.map(user => {
      return String(user.id);
    }) || [],
  );
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>(
    subsystem?.users?.map(user => {
      return {
        id: Number(user.id),
        username: user?.username || '',
        email: user?.email || '',
        avatar: user?.avatar_url || '',
        scoped_role_id: user.scoped_role_id || 1, // Берем существующую роль или "1" по умолчанию
      };
    }) || [],
  );
  const usersArr: IUser[] = useSelector(users);
  const rolesArr: IRole[] = useSelector(roles);
  const adminOptions = usersArr
    .filter(user => !administrators?.some(admin => admin.id === user.id))
    .map(user => {
      return {
        title: user.username,
        subtitle: user.email,
        value: String(user.id),
        avatar: user.avatar_url,
      };
    });
  const defaultRoleOptions = rolesArr
    .map(role => {
      return {title: role.name, value: String(role.id)};
    })
    .filter(role => {
      if (
        currentRole === EUserRoles.BASE_USER &&
        currentScopedRole !== EUserScopedRoles.ADMIN
      ) {
        return role.value !== String(EUserScopedRoles.ADMIN);
      }
      return true;
    });
  const roleOptions = rolesArr
    .map(role => {
      return {title: role.name, value: String(role.id)};
    })
    .filter(role => {
      if (
        currentRole === EUserRoles.BASE_USER &&
        currentScopedRole !== EUserScopedRoles.ADMIN
      ) {
        return role.value !== String(EUserScopedRoles.ADMIN);
      }
      return true;
    })
    .concat({title: 'Удалить', value: 'delete'});

  useEffect(() => {
    if (subsystem?.name) {
      setSubsystemName(subsystem.name);
    } else {
      setSubsystemName('');
    }
  }, [subsystem]);

  useEffect(() => {
    setSelectedUsers(prevUsers => {
      const existingRoles = new Map(
        prevUsers.map(user => [user.id, user.scoped_role_id]),
      );
      return selectedUsersIDs.map(id => {
        const selectedUser = usersArr.find(user => String(user.id) === id);
        return {
          id: Number(id),
          username: selectedUser?.username || '',
          email: selectedUser?.email || '',
          avatar: selectedUser?.avatar_url || '',
          scoped_role_id: existingRoles.get(Number(id)) || 1, // Берем существующую роль или "1" по умолчанию
        };
      });
    });
  }, [selectedUsersIDs]);

  const updateUserRole = (userId: string, newRole: string) => {
    if (newRole === 'delete') {
      setSelectedUsersIDs(prevIDs => prevIDs.filter(id => id !== userId));
    } else {
      setSelectedUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === Number(userId) ? {...user, scoped_role_id: Number(newRole)} : user,
        ),
      );
    }
  };

  const onSave = () => {
    if (systemID) {
      dispatch(
        addSubsystem({system_id: systemID, users: selectedUsers, name: subsystemName}),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast
              title="Готово!"
              text="Подсистема добавлена и настроена"
              type="success"
            />,
          );
          setSubsystemName('');
          setSelectedUsers(
            subsystem?.users?.map(user => {
              return {
                id: Number(user.id),
                username: user?.username || '',
                email: user?.email || '',
                avatar: user?.avatar_url || '',
                scoped_role_id: user.scoped_role_id || 1, // Берем существующую роль или "1" по умолчанию
              };
            }) || [],
          );
          setSelectedUsersIDs(
            subsystem?.users?.map(user => {
              return String(user.id);
            }) || [],
          );
          onCloseRequest();
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось создать подсистему'}
              type="error"
            />,
          );
        });
    } else if (subsystem) {
      dispatch(
        editSubsystem({
          subsystem_id: subsystem.id,
          users: selectedUsers,
          name: subsystemName,
        }),
      )
        .unwrap()
        .then(() => {
          toast.success(
            <Toast title="Готово!" text="Подсистема успешно обновлена" type="success" />,
          );
          onCloseRequest();
        })
        .catch((err: any) => {
          toast.error(
            <Toast
              title="Ошибка"
              text={err?.message || 'Не удалось обновить подсистему'}
              type="error"
            />,
          );
        });
    }
  };

  return (
    <ModalWindow isOpen={isOpened} overflowIsVisible>
      <ModalWrapper>
        <ModalHeader
          title={systemID ? 'Добавить подсистему' : 'Редактировать подсистему'}
          onClose={onCloseRequest}
        />
        <ModalContent>
          <Input
            id="subsystem-name"
            label="Название подсистемы"
            value={subsystemName}
            onChange={e => {
              setSubsystemName(e.currentTarget.value);
            }}
            withoutErrorText
            hint="Максимум 50 символов"
            maxLength={50}
          />
          <MultiSelect
            id="system-users"
            label="Добавить пользователей"
            value={selectedUsersIDs}
            options={adminOptions}
            onChange={setSelectedUsersIDs}
          />
          <AddSubsystemUsersWrapper>
            {!!administrators?.length && (
              <DefaultUsersWrapper addedUsers={selectedUsersIDs}>
                {administrators.map(administrator => {
                  return (
                    <DefaultUsersRow key={`default-user-${administrator.id}`}>
                      <DefaultUsersInfoWrapper>
                        <SubdirectoryWrapper
                          onMouseEnter={() => setTooltipVisibleId(administrator.id)}
                          onMouseLeave={() => setTooltipVisibleId(null)}>
                          <Subdirectory size={16} color={theme.colors.basic07} />
                          {!!administrator?.scoped_role_id && (
                            <Tooltip
                              isVisible={tooltipVisibleId === administrator.id}
                              text={userScopedRoles[administrator.scoped_role_id]}
                              displayOnTop={false}
                            />
                          )}
                        </SubdirectoryWrapper>
                        <Avatar img={administrator.avatar_url || ''} />
                        <DefaultUsersTextWrapper>
                          <Text
                            text={administrator.username || ''}
                            type="tinyM"
                            color={theme.colors.basic01}
                          />
                          <Text
                            text={administrator.email || ''}
                            type="tinyR"
                            color={theme.colors.basic02}
                          />
                        </DefaultUsersTextWrapper>
                      </DefaultUsersInfoWrapper>
                      <OptionButton
                        value={String(administrator?.scoped_role_id) || '1'}
                        options={defaultRoleOptions}
                        fixedWidth={160}
                        withoutIcon
                        onChange={value =>
                          updateUserRole(String(administrator.id), value)
                        }
                      />
                    </DefaultUsersRow>
                  );
                })}
              </DefaultUsersWrapper>
            )}
            {!!selectedUsers.length && (
              <SelectedUsersWrapper>
                {selectedUsersIDs.map(selectedUserID => {
                  const selectedUser = selectedUsers.find(
                    user => String(user.id) === selectedUserID,
                  );
                  return (
                    <SelectedUsersRow key={`sub-selected-user-${selectedUserID}`}>
                      <SelectedUsersInfoWrapper>
                        <Avatar img={selectedUser?.avatar_url || ''} />
                        <SelectedUsersTextWrapper>
                          <Text
                            text={selectedUser?.username || ''}
                            type="tinyM"
                            color={theme.colors.basic01}
                          />
                          <Text
                            text={selectedUser?.email || ''}
                            type="tinyR"
                            color={theme.colors.basic02}
                          />
                        </SelectedUsersTextWrapper>
                      </SelectedUsersInfoWrapper>
                      <OptionButton
                        value={String(selectedUser?.scoped_role_id) || '1'}
                        options={roleOptions}
                        fixedWidth={160}
                        withoutIcon
                        onChange={value => updateUserRole(selectedUserID, value)}
                      />
                    </SelectedUsersRow>
                  );
                })}
              </SelectedUsersWrapper>
            )}
          </AddSubsystemUsersWrapper>
        </ModalContent>
        <ButtonsWrapper>
          <Button
            onClick={onSave}
            fullWidth
            title={!subsystem?.id ? 'Добавить' : 'Сохранить'}
            variant="filled"
            disabled={!selectedUsersIDs.length}
          />
          <Button
            fullWidth
            title="Закрыть"
            onClick={() => {
              onCloseRequest();
            }}
          />
        </ButtonsWrapper>
      </ModalWrapper>
    </ModalWindow>
  );
};

export default AddEditSubsystemModal;
